import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {boundMethod} from "autobind-decorator";
import React from "react";
import {FormattedMessage} from "react-intl";
import {Link, Redirect} from "react-router-dom";

import http from "@/services/http";
import {IPendingTaskModel} from "@toolbox/button-like/models";

import DeleteButton from "@toolbox/button-like/DeleteButton";
import ContainerCol from "@toolbox/display-blocks/ContainerCol";
import T from "@translate/T";

// simplifies the specified name to lowercase & shortens the verification
export function getVerificationName(str: string) {
    const lower = str.toLowerCase();
    const words = lower.split(" ");

    return words.length <= 6 ? lower : words.slice(0, 6).join(" ");
}

export interface IDeleteProjectProps {
    id: number;
    name: string;
}

interface IDeleteProjectState {
    isDeleted: boolean;
    userInput: string;
}

class DeleteProject extends React.PureComponent<
    IDeleteProjectProps,
    IDeleteProjectState
> {
    public readonly state: IDeleteProjectState = {
        isDeleted: false,
        userInput: "",
    };

    private readonly ref = React.createRef<DeleteButton>();

    private get isConfirmed() {
        const {name} = this.props;
        const {userInput} = this.state;

        return userInput === getVerificationName(name);
    }

    @boundMethod
    public setUserInput(e: React.ChangeEvent<HTMLInputElement>) {
        e.preventDefault();

        this.setState({userInput: e.target.value});
    }

    public render() {
        const {isDeleted, userInput} = this.state;

        if (isDeleted) {
            return <Redirect to="/" push={true} />;
        }

        const {id, name} = this.props;
        const required = <kbd>{getVerificationName(name)}</kbd>;
        const projectName = <em className="text-warning"> {name} </em>;

        return (
            <ContainerCol col={6}>
                <h3>
                    <T>Delete project</T>
                </h3>
                <hr />

                <div className="form-text">
                    <strong>
                        <FormattedMessage
                            id="You are going to delete project {name} and all its data. This action cannot be reversed."
                            defaultMessage="You are going to delete project {name} and all its data. This action cannot be reversed."
                            values={{name: projectName}}
                        />
                    </strong>
                </div>

                <div className="mb-2">
                    <label className="mb-1" htmlFor="project-name">
                        <FormattedMessage
                            id="Enter the project name {required} in the textbox below to confirm."
                            defaultMessage="Enter the project name {required} in the textbox below to confirm."
                            values={{required}}
                        />
                    </label>
                    <input
                        type="text"
                        id="project-name"
                        autoComplete="off"
                        className="form-control"
                        required={true}
                        value={userInput}
                        onChange={this.setUserInput}
                    />
                </div>

                <div className="d-flex">
                    <DeleteButton
                        ref={this.ref}
                        id="delete"
                        classNameButton="mr-auto"
                        disabled={!this.isConfirmed}
                        noAnimation={true}
                        skipConfirm={true}
                        onDelete={this.delete}
                    />

                    <Link to={`/project/${id}/edit`}>
                        <button type="button" className="btn btn-secondary">
                            <FontAwesomeIcon
                                icon={faTimes}
                                fixedWidth={true}
                                className="mr-1"
                            />
                            <T>Cancel</T>
                        </button>
                    </Link>
                </div>
            </ContainerCol>
        );
    }

    @boundMethod
    private async delete() {
        try {
            await http
                .delete("/api/projects/" + this.props.id)
                .json<IPendingTaskModel>();

            this.setState({isDeleted: true});

            return true;
        } catch (error) {
            this.ref.current!.extraText = null;
            if (error instanceof http.HTTPError) {
                if (error.response.status === 409) {
                    this.ref.current!.extraText = (
                        <React.Fragment>
                            <br />
                            <T>
                                You can not delete a project while a measurement
                                is running.
                            </T>
                        </React.Fragment>
                    );
                }
            }

            return false;
        }
    }
}

export default DeleteProject;
